import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { groupsApi } from 'src/shared/api/groupsApi';
import TableService from 'src/shared/api/table/TableService';
import type {
  AuditUserInfo,
  FormAccessPermissionItem,
  FormAccessPermissionLevel,
  ReplaceFormAccessPermissionItem,
} from 'src/shared/api/table/types';
import { GroupUser } from 'src/shared/types/groups';
import { Button, Modal, Select, SelectItemType } from 'src/shared/ui';
import { Loader } from 'src/shared/ui/Loader';

interface FormAccessManagementModalProps {
  onClose: () => void;
  tableId: number;
  formLabel?: string;
}

type FormAccessDraftPermission = {
  user_id: number;
  can_read: boolean;
  can_edit: boolean;
  user?: AuditUserInfo | null;
};

const permissionLevelOptions: SelectItemType[] = [
  { value: 'read', label: 'Только чтение' },
  { value: 'edit', label: 'Редактирование' },
];

const formatUserDisplayName = (user?: AuditUserInfo | null): string => {
  const displayName = user?.display_name?.trim();
  if (displayName) {
    return displayName;
  }

  const fullName = user?.full_name?.trim();
  if (fullName) {
    return fullName;
  }

  const userName = user?.user_name?.trim();
  if (userName) {
    return userName;
  }

  const email = user?.email?.trim();
  if (email) {
    return email;
  }

  if (typeof user?.id === 'number' && user.id > 0) {
    return `ID ${user.id}`;
  }

  return 'Неизвестный пользователь';
};

const buildAuditUserInfo = (user: GroupUser): AuditUserInfo => {
  const fullName = user.full_name?.trim();
  const userName = user.user_name?.trim();
  const email = user.email?.trim();

  return {
    id: user.id,
    full_name: fullName || undefined,
    user_name: userName || undefined,
    email: email || undefined,
    display_name: fullName || userName || email || `ID ${user.id}`,
  };
};

const mapPermissionToDraft = (permission: FormAccessPermissionItem): FormAccessDraftPermission => ({
  user_id: permission.user_id,
  can_read: permission.can_read,
  can_edit: permission.can_edit,
  user: permission.user,
});

const normalizePermissionsPayload = (
  permissions: FormAccessDraftPermission[],
): ReplaceFormAccessPermissionItem[] =>
  [...permissions]
    .map(permission => ({
      user_id: permission.user_id,
      can_read: permission.can_read,
      can_edit: permission.can_edit,
    }))
    .sort((left, right) => left.user_id - right.user_id);

const getPermissionLevel = (permission: FormAccessDraftPermission): FormAccessPermissionLevel =>
  permission.can_edit ? 'edit' : 'read';

const getCandidateUserLabel = (user: GroupUser): string => {
  const fullName = user.full_name?.trim();
  if (fullName) {
    return fullName;
  }

  const userName = user.user_name?.trim();
  if (userName) {
    return userName;
  }

  const email = user.email?.trim();
  if (email) {
    return email;
  }

  return `ID ${user.id}`;
};

export const FormAccessManagementModal: FC<FormAccessManagementModalProps> = ({
  onClose,
  tableId,
  formLabel,
}) => {
  const [loadedPermissions, setLoadedPermissions] = useState<FormAccessDraftPermission[]>([]);
  const [draftPermissions, setDraftPermissions] = useState<FormAccessDraftPermission[]>([]);
  const [users, setUsers] = useState<GroupUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedPermissionLevel, setSelectedPermissionLevel] =
    useState<FormAccessPermissionLevel>('read');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [saveErrorMessage, setSaveErrorMessage] = useState('');

  const contextLabel = useMemo(() => {
    const normalizedLabel = formLabel?.trim();
    if (normalizedLabel) {
      return normalizedLabel;
    }

    return `Форма ID ${tableId}`;
  }, [formLabel, tableId]);

  const userOptions = useMemo<SelectItemType[]>(() => {
    const options = users
      .map(user => ({
        value: String(user.id),
        label: getCandidateUserLabel(user),
      }))
      .sort((left, right) => left.label.localeCompare(right.label, 'ru', { sensitivity: 'base' }));

    return [{ value: '', label: 'Выберите пользователя' }, ...options];
  }, [users]);

  const selectedDraftPermission = useMemo(() => {
    const resolvedUserId = Number(selectedUserId);
    if (!Number.isFinite(resolvedUserId) || resolvedUserId <= 0) {
      return null;
    }

    return draftPermissions.find(permission => permission.user_id === resolvedUserId) || null;
  }, [draftPermissions, selectedUserId]);

  const hasUnsavedChanges = useMemo(() => {
    return (
      JSON.stringify(normalizePermissionsPayload(loadedPermissions)) !==
      JSON.stringify(normalizePermissionsPayload(draftPermissions))
    );
  }, [draftPermissions, loadedPermissions]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');
    setSaveErrorMessage('');

    try {
      const [{ data: accessData }, { data: usersData }] = await Promise.all([
        TableService.getFormAccessPermissions(tableId),
        groupsApi.getUsers(),
      ]);

      const normalizedPermissions = (accessData?.permissions || []).map(mapPermissionToDraft);
      setLoadedPermissions(normalizedPermissions);
      setDraftPermissions(normalizedPermissions);
      setUsers(usersData || []);
    } catch (error) {
      console.error('Не удалось загрузить права доступа формы', error);
      setErrorMessage('Не удалось загрузить права доступа формы');
    } finally {
      setIsLoading(false);
    }
  }, [tableId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedDraftPermission) {
      return;
    }

    setSelectedPermissionLevel(getPermissionLevel(selectedDraftPermission));
  }, [selectedDraftPermission]);

  const handleApplyDraftPermission = () => {
    const resolvedUserId = Number(selectedUserId);
    if (!Number.isFinite(resolvedUserId) || resolvedUserId <= 0) {
      setSaveErrorMessage('Выберите пользователя');
      return;
    }

    const selectedUser = users.find(user => user.id === resolvedUserId);
    if (!selectedUser) {
      setSaveErrorMessage('Не удалось найти выбранного пользователя');
      return;
    }

    const nextPermission: FormAccessDraftPermission = {
      user_id: resolvedUserId,
      can_read: true,
      can_edit: selectedPermissionLevel === 'edit',
      user: buildAuditUserInfo(selectedUser),
    };

    setDraftPermissions(currentPermissions => {
      const existingIndex = currentPermissions.findIndex(
        permission => permission.user_id === resolvedUserId,
      );

      if (existingIndex === -1) {
        return [...currentPermissions, nextPermission];
      }

      const nextPermissions = [...currentPermissions];
      nextPermissions[existingIndex] = nextPermission;
      return nextPermissions;
    });
    setSaveErrorMessage('');
  };

  const handleSetRowPermission = (userId: number, level: FormAccessPermissionLevel) => {
    setDraftPermissions(currentPermissions =>
      currentPermissions.map(permission =>
        permission.user_id === userId
          ? {
              ...permission,
              can_read: true,
              can_edit: level === 'edit',
            }
          : permission,
      ),
    );
    setSaveErrorMessage('');
  };

  const handleRemovePermission = (userId: number) => {
    setDraftPermissions(currentPermissions =>
      currentPermissions.filter(permission => permission.user_id !== userId),
    );
    if (Number(selectedUserId) === userId) {
      setSelectedUserId('');
      setSelectedPermissionLevel('read');
    }
    setSaveErrorMessage('');
  };

  const handleResetDraft = () => {
    setDraftPermissions(loadedPermissions);
    setSaveErrorMessage('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveErrorMessage('');

    try {
      await TableService.replaceFormAccessPermissions(tableId, {
        permissions: normalizePermissionsPayload(draftPermissions),
      });
      await loadData();
    } catch (error) {
      console.error('Не удалось сохранить права доступа формы', error);
      setSaveErrorMessage('Не удалось сохранить права доступа формы. Повторите попытку или перезагрузите данные.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="flex w-[64rem] max-w-[90dvw] flex-col">
        <div className="font-gothampro-500 mb-2 text-2xl text-green-100">
          Настроить права доступа
        </div>
        <div className="font-gothampro-400 mb-5 text-sm dark:text-white">{contextLabel}</div>

        {isLoading && (
          <div className="flex h-48 items-center justify-center">
            <Loader classNames="w-10 h-10" />
          </div>
        )}

        {!isLoading && errorMessage && (
          <div className="rounded border border-pink-100 p-4">
            <div className="font-gothampro-400 text-sm text-pink-100">{errorMessage}</div>
            <div className="mt-4 flex gap-2">
              <Button
                size="small"
                classNames="w-32"
                variant="violetOutline"
                onClick={() => void loadData()}
              >
                Повторить
              </Button>
              <Button size="small" classNames="w-32" variant="greyLight" onClick={onClose}>
                Закрыть
              </Button>
            </div>
          </div>
        )}

        {!isLoading && !errorMessage && (
          <>
            <div className="mb-5 rounded border border-grey-1000 p-4">
              <div className="font-gothampro-400 mb-2 text-sm dark:text-white">
                Если список прав пуст, форма наследует проектный доступ.
              </div>

              <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_auto] gap-3">
                <Select
                  value={selectedUserId}
                  options={userOptions}
                  disabled={isSaving}
                  onChange={option => setSelectedUserId(option.value)}
                />
                <Select
                  value={selectedPermissionLevel}
                  options={permissionLevelOptions}
                  disabled={isSaving}
                  onChange={option =>
                    setSelectedPermissionLevel(option.value as FormAccessPermissionLevel)
                  }
                />
                <Button
                  classNames="w-44"
                  variant="violet"
                  onClick={handleApplyDraftPermission}
                  disabled={isSaving}
                >
                  {selectedDraftPermission ? 'Обновить право' : 'Добавить право'}
                </Button>
              </div>
            </div>

            {saveErrorMessage && (
              <div className="mb-4 rounded border border-pink-100 p-3 text-sm text-pink-100">
                {saveErrorMessage}
              </div>
            )}

            <div className="mb-5 max-h-[26rem] overflow-y-auto rounded border border-grey-1000 p-2">
              {!draftPermissions.length && (
                <div className="font-gothampro-400 p-2 text-sm opacity-70 dark:text-white">
                  Явные права доступа ещё не настроены.
                </div>
              )}

              {draftPermissions
                .slice()
                .sort((left, right) =>
                  formatUserDisplayName(left.user).localeCompare(formatUserDisplayName(right.user), 'ru', {
                    sensitivity: 'base',
                  }),
                )
                .map(permission => (
                  <div
                    key={`permission-${permission.user_id}`}
                    className="mb-2 grid grid-cols-[minmax(0,1.5fr)_auto_auto_auto] items-center gap-3 rounded border border-grey-1000 p-3"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm dark:text-white">
                        {formatUserDisplayName(permission.user)}
                      </div>
                      <div className="truncate text-extra-xs opacity-60 dark:text-white">
                        {permission.user?.email || `ID ${permission.user_id}`}
                      </div>
                    </div>

                    <div className="rounded-full bg-grey-1400 px-3 py-1 text-extra-xs text-white">
                      {permission.can_edit ? 'Редактирование' : 'Только чтение'}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="small"
                        classNames="w-32"
                        variant={permission.can_edit ? 'greyLight' : 'violetOutline'}
                        onClick={() => handleSetRowPermission(permission.user_id, 'read')}
                        disabled={isSaving || !permission.can_edit}
                      >
                        Только чтение
                      </Button>
                      <Button
                        size="small"
                        classNames="w-32"
                        variant={permission.can_edit ? 'violet' : 'violetOutline'}
                        onClick={() => handleSetRowPermission(permission.user_id, 'edit')}
                        disabled={isSaving || permission.can_edit}
                      >
                        Редактирование
                      </Button>
                    </div>

                    <Button
                      size="small"
                      classNames="w-24"
                      variant="greyLight"
                      onClick={() => handleRemovePermission(permission.user_id)}
                      disabled={isSaving}
                    >
                      Удалить
                    </Button>
                  </div>
                ))}
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                size="small"
                classNames="w-44"
                variant="greyLight"
                onClick={handleResetDraft}
                disabled={isSaving || !hasUnsavedChanges}
              >
                Сбросить черновик
              </Button>
              <Button size="small" classNames="w-28" variant="greyLight" onClick={onClose}>
                Отмена
              </Button>
              <Button
                size="small"
                classNames="w-32"
                variant="violet"
                onClick={() => void handleSave()}
                disabled={isSaving || !hasUnsavedChanges}
              >
                {isSaving ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
